/**
 * 카드 조회 API — 검색·상세·일괄(스캔 결과).
 *
 * BE endpoint (모두 permitAll — 비회원 스캔도 조회 가능):
 *   GET /api/v1/user/cards/batch?cardSerialNos=1,2,3
 *   GET /api/v1/user/cards/{cardSerialNo}
 *
 * ⚠ `cardSerialNo` 는 수집서버 채번 전역 ID 이고 **온디바이스 매처가 반환하는 키와 같다**.
 *   다만 인덱스와 카탈로그는 세대가 달라 인식은 됐는데 카탈로그에 없는 카드가 있다
 *   (2026-08-03 실측: 인덱스 57,726장 vs 적재 12,993장). batch 는 없는 카드를 조용히
 *   빼고 돌려주므로, 호출부는 **요청 수 < 응답 수** 를 정상 경로로 다뤄야 한다.
 */
import { apiGet, apiPost } from "./client";
import { env } from "../config/env";

export type PriceDisplay = "CURRENT" | "REFERENCE_CASE" | "NONE";

export interface PriceView {
  /** null = 표시할 시세 없음(display NONE) */
  amount: number | null;
  currency: string | null;
  reliability: string;
  observationCount: number;
  display: PriceDisplay;
  trendStatus: string;
  trendRate: number | null;
  trendBaseDate: string | null;
  /**
   * 이 시세를 마지막으로 갱신한 시각(ISO). 24시간이 넘으면 화면이 '오래된 시세' 로
   * 경고한다 (SC-007 · FN-016).
   *
   * ⚠ 서버가 "몇 시간 전"이 아니라 **시각**을 준다 — 상대 시간을 서버에서 굳히면
   *   응답이 캐시되는 순간 그 값이 늙지 않는다(트렌딩은 실제로 캐시된다).
   * ⚠ 시세가 없으면 null. 구 서버는 이 필드 자체를 안 보내므로 optional 이다.
   */
  refreshedAt?: string | null;
}

/**
 * 카탈로그 이미지 경로 → 표시용 절대 URL.
 *
 * IMG_PATHWY 는 상대 키(`card/{serlno}/front-{hash}.jpg`)로 저장된다
 * (adbe CardImageCopyService.buildRelativePath). 오리진은 `VITE_CARD_IMAGE_BASE_URL`.
 * 다만 향후 적재 방식이 절대 URL 로 바뀔 수도 있어 http(s) 로 시작하면 그대로 통과시킨다.
 * base 미설정 + 상대 경로면 null — 호출부는 플레이스홀더로 처리한다.
 */
export function cardImageUrl(imagePathway: string | null | undefined): string | null {
  if (!imagePathway) return null;
  if (/^https?:\/\//i.test(imagePathway)) return imagePathway;
  const base = env.VITE_CARD_IMAGE_BASE_URL;
  if (!base) return null;
  return `${base.replace(/\/+$/, "")}/${imagePathway.replace(/^\/+/, "")}`;
}

export interface CardItem {
  cardSerialNo: number;
  cardUid: string;
  name: string;
  /** 컬렉터 번호 — 번호 없는 인쇄판(일본판 기본 에너지 등)이 실재해 null 이 온다 */
  number: string | null;
  language: string;
  /** 레어도 — 카탈로그 미기재면 null */
  rarity: string | null;
  /**
   * 배리언트 표시명 — "미러" / "마스터볼 미러". **기본판은 null 이다**(서버가 접어서 내린다).
   * 수집서버가 미러·마스터볼을 별개 인쇄판으로 내려주는데 이름·번호·레어도가 전부 같아
   * (실 배치 510쌍) 이 값이 없으면 목록에 똑같은 카드가 두 장 나온다. 값이 있을 때만 배지를 띄운다.
   * 구 서버 응답에는 없어 옵셔널이다.
   */
  variantLabel?: string | null;
  /** 카드 이미지 — 이미지 번들 적재 전에는 null */
  imagePathway: string | null;
  cardsetSerialNo: number;
  cardsetName: string;
  cardsetCode: string;
  /**
   * 기술명 — 카드 언어 그대로. 포켓몬이 아닌 카드(트레이너·에너지)는 빈 배열이고 그게 정상이다.
   * 구 서버 응답에는 아예 없을 수 있어 옵셔널로 둔다.
   */
  attackNames?: string[];
  /**
   * 진화 단계 코드 — BASIC/STAGE_1/STAGE_2/V_EVOLUTION/MEGA/BREAK/LEVEL_UP.
   * 표시 문구는 화면이 i18n 으로 만든다(원천 표기가 언어마다 달라 그대로 쓰면 섞인다).
   * 진화가 없는 카드(트레이너·에너지)와 상세를 못 뽑은 카드는 null 이다.
   */
  evolutionStage?: string | null;
  listPrice: PriceView;
}

/**
 * 스캔 결과 일괄 조회. 카탈로그에 없는 ID 는 응답에서 빠진다.
 * @returns cardSerialNo → CardItem 맵 (호출부가 매칭 순서를 유지하며 조회)
 */
export async function fetchCardsByIds(cardSerialNos: number[]): Promise<Map<number, CardItem>> {
  if (cardSerialNos.length === 0) return new Map();
  const items = await apiGet<CardItem[] | null>("/cards/batch", { cardSerialNos });
  return new Map((items ?? []).map((item) => [item.cardSerialNo, item]));
}

/**
 * 카드 목록 정렬 (SC-006f).
 *
 * ⚠ 화면정의서의 **희귀도·HP 정렬은 없다** — 희귀도는 카탈로그에 60종이 넘는데 서열 정보가
 * 없어 "높은 순"을 정의할 수 없고, HP 는 컬럼 자체가 없다(수집서버가 능력치를 주지 않는다).
 */
export type CardSortKey = "NUMBER" | "NAME" | "PRICE_DESC" | "PRICE_ASC";

export interface CardSearchParams {
  /** 카드명 부분일치 또는 컬렉터 번호 정확일치. 비우면 전체 목록 */
  keyword?: string;
  /** 앱의 "카드 에디션" 선택 (ko | en | ja) */
  language?: string;
  cardsetSerialNo?: number;
  /** SC-006d 희귀도 다중 선택 */
  rarities?: string[];
  /** SC-006a 가격 범위. 시세가 없는 카드는 범위 조건에서 함께 빠진다 */
  minPrice?: number;
  maxPrice?: number;
  sort?: CardSortKey;
  page?: number;
  size?: number;
}

/**
 * 검색 응답.
 *
 * ⚠ 전체 건수(total)가 없다 — BE 가 COUNT 를 피하려고 size+1 을 읽어 `hasNext` 만 판정한다.
 * 화면에서 "결과 N건"을 단정적으로 쓸 수 없고, 받은 만큼만 셀 수 있다.
 */
export interface CardSearchResponse {
  items: CardItem[];
  page: number;
  size: number;
  hasNext: boolean;
}

/**
 * 카드 상세 열람 1회 기록 (FN-048 · SC-007) — 홈 §2 칩의 조회수 축.
 *
 * ⚠ 상세 조회(fetchCardDetail)와 **따로** 부른다. 그 API 는 스캔 요약의 시세 조회와
 *   react-query 재조회도 쓰는데, 그것까지 세면 사람이 본 적 없는 카드가 인기 카드가 된다.
 * ⚠ 실패해도 조용하다. 세지 못한 것은 사용자가 알 필요도, 할 수 있는 일도 없다.
 */
export function recordCardView(cardSerialNo: number): Promise<void> {
  return apiPost<void>(`/cards/${cardSerialNo}/view`).catch(() => undefined);
}

export async function searchCards(params: CardSearchParams): Promise<CardSearchResponse> {
  const res = await apiGet<CardSearchResponse | null>("/cards", {
    keyword: params.keyword,
    language: params.language,
    cardsetSerialNo: params.cardsetSerialNo,
    // 빈 배열을 보내면 axios 가 `rarities=` 를 붙여 BE 가 빈 문자열 하나로 읽는다.
    rarities: params.rarities?.length ? params.rarities : undefined,
    minPrice: params.minPrice,
    maxPrice: params.maxPrice,
    sort: params.sort,
    page: params.page,
    size: params.size,
  });
  return res ?? { items: [], page: params.page ?? 0, size: params.size ?? 0, hasNext: false };
}

/**
 * 카드세트(팩) 요약.
 *
 * ⚠ `seriesName`·`releasedOn`·이미지 경로는 **null 이 정상**이다 — 수집서버가 아직 안 채워
 * 보낸다(2026-08-05 실측: 시리즈명 565세트 전부 null, 발매일은 영문판에만 존재).
 * 화면은 이 값들이 없다는 전제로 그려야 한다.
 */
export interface CardsetSummary {
  cardsetSerialNo: number;
  name: string;
  code: string;
  seriesName: string | null;
  language: string;
  /** 도감 진행률 분모 — 정책 필터 이전의 canonical 장수 */
  totalCardCount: number;
  releasedOn: string | null;
  logoImagePathway: string | null;
  symbolImagePathway: string | null;
}

/** 세트 목록 — 페이지네이션 없음(언어별 수십~수백 건으로 유계). */
export async function fetchCardsets(language?: string): Promise<CardsetSummary[]> {
  return (await apiGet<CardsetSummary[] | null>("/cardsets", { language })) ?? [];
}

/**
 * 세트 상세 — 헤더 정보 + 필터 옵션.
 *
 * `rarities` 는 **그 세트에 실제로 있는** 희귀도만 담는다(SC-006d). 카드 목록에서 유도하면
 * 불러온 페이지에만 있는 값이 나와 스크롤할 때마다 필터 칩이 늘어난다. 희귀도 미기재 카드만
 * 있는 세트에서는 빈 배열이 정상이다.
 */
export interface CardsetDetail {
  cardset: CardsetSummary;
  rarities: string[];
}

export async function fetchCardsetDetail(cardsetSerialNo: number): Promise<CardsetDetail> {
  return apiGet<CardsetDetail>(`/cardsets/${cardsetSerialNo}`);
}

/**
 * 배리언트별 시세 — 피니시·패턴·감정등급 축 (기획확정안 §5.4).
 *
 * 감정사·점수가 다르면 **별개 가격**이라 합치지 않는다. BE 가 표시할 시세가 없는
 * 배리언트는 빼고 내려보내므로, 빈 배열이면 "등급별 가격 없음"이 정상이다.
 */
export interface VariantPrice {
  finish: string;
  pattern: string;
  grader: string;
  grade: string;
  price: PriceView;
}

/**
 * 카드 상세.
 *
 * ⚠ 이 응답에 **없는 것**: 가격 이력(그래프), 호가(ask/bid), 실거래 범위(low/high),
 * 출처별 건수, 수입 프리미엄(현지↔국내 비교). 화면정의서 SC-007 은 이것들을 그리지만
 * 수집서버가 아직 안 준다 — 없는 값을 만들어내지 말고 "준비 중"으로 비워 둘 것.
 */
export interface CardDetail {
  cardSerialNo: number;
  cardUid: string;
  /** 언어판 그룹 키 — 같은 그림의 한·일·영 인쇄판을 묶는다(§5.2 비교 전용) */
  groupKey: string | null;
  name: string;
  number: string | null;
  printedNumber: string | null;
  language: string;
  rarity: string | null;
  /** 배리언트 표시명 — 기본판은 null. CardItem.variantLabel 과 같은 규약 */
  variantLabel?: string | null;
  /**
   * 카드 유형 — POKEMON | TRAINER | ENERGY. 카탈로그 97.3% 에 있고 없으면 null.
   * ⚠ optional 이다 — 구 서버는 이 필드를 안 보낸다(fail-open).
   */
  cardType?: string | null;
  /**
   * 에너지 타입 — GRASS·FIRE·WATER…. **포켓몬 카드에만 있다**(트레이너·에너지는 빈 배열).
   * δ 카드처럼 두 타입인 인쇄판이 있어 배열이다. 서버가 대문자로 눌러 보낸다.
   */
  energyTypes?: string[];
  artist: string | null;
  imagePathway: string | null;
  largeImagePathway: string | null;
  cardset: CardsetSummary;
  listPrice: PriceView;
  /**
   * 사면(최저 호가) — SC-007 '지금 거래하면'.
   *
   * ⚠ **팔면(BID)은 없다.** 수집 계약의 관측 종류가 ASK|SOLD 뿐이라 구매희망가라는 축이
   *   아예 안 온다 → design 의 '차이(스프레드)'도 못 낸다.
   * ⚠ 대표가와 **금액이 같으면 null** 이다 — 같은 값을 두 번 쓰지 않는다.
   */
  askQuote?: AskQuote | null;
  variantPrices: VariantPrice[];
}

export interface AskQuote {
  amount: number | null;
  currency: string | null;
  observedOn: string | null;
  observationCount: number;
}

/** 시세 이력의 한 점 — 관측이 있는 날만 온다(날짜 간격이 고르지 않다). */
export interface PricePoint {
  date: string;
  amount: number | null;
}

/** design HISTORY_PERIODS 와 같은 값 — BE 가 이 셋만 받는다. */
export const HISTORY_PERIODS = [7, 30, 90] as const;
export type HistoryPeriod = (typeof HISTORY_PERIODS)[number];

export interface PriceHistory {
  /**
   * 이 시리즈가 무엇인가 — SOLD(판매완료) | ASK(호가).
   *
   * ⚠ 화면 라벨을 이걸로 고른다. 한국판은 **69.4%** 가 체결 없이 호가뿐이라, 전부
   *   "거래"라고 부르면 대부분의 한국 카드에서 거짓말이 된다.
   * ⚠ SOLD 도 실결제금액이 아니라 **판매완료 시점의 게시가격**이다(DB 컬럼 주석) —
   *   "실거래가"로 쓰지 말 것.
   */
  observationKind: string;
  windowDays: number;
  points: PricePoint[];
  /** 첫 점 대비 마지막 점의 변화율(%). 점이 2개 미만이면 null — 0 이 아니다. */
  changeRate: number | null;
  low: number | null;
  high: number | null;
  observationCount: number;
  lastObservedOn: string | null;
  currency: string;
}

/**
 * 시세 이력 (SC-007 히스토리).
 *
 * 상세와 나눠 부르는 이유는 기간 스위치가 재조회하기 때문이다 — 7/30/90 을 상세에 다
 * 실으면 대부분 안 보는 두 벌을 매번 같이 받게 된다.
 * ⚠ 시세가 없는 카드는 **null** 이 온다(404 아님).
 */
export async function fetchCardPriceHistory(
  cardSerialNo: number,
  days: HistoryPeriod,
): Promise<PriceHistory | null> {
  return apiGet<PriceHistory | null>(`/cards/${cardSerialNo}/price-history`, { days });
}

export async function fetchCardDetail(cardSerialNo: number): Promise<CardDetail> {
  return apiGet<CardDetail>(`/cards/${cardSerialNo}`);
}

/**
 * 홈 §2 트렌딩 칩 (FN-048).
 *
 * ⚠ v1 은 전부 **시세 데이터 기반 근사치**다 — 행동 로그(스캔·조회·담기)가 없어서
 * HOT=거래 관측 많은 순, GAINERS=등락률 순, STEADY=신뢰도 HIGH 관측 순으로 정의한다.
 * 가격이 안 실린 환경(현재 로컬)에서는 빈 목록이 정답이다.
 */
export type TrendChipKey = "HOT" | "GAINERS" | "STEADY";

export async function fetchTrendingCards(params: {
  chip: TrendChipKey;
  language?: string;
  limit?: number;
}): Promise<CardItem[]> {
  return (await apiGet<CardItem[] | null>("/cards/trending", params)) ?? [];
}
